// WO-17 PR-B: Click-logging hook for the Peerlist jobs funnel.
//
// Contract:
//   const log = useApplyClickLogger();
//   const clickId = await log({ job, kind: 'optimize_and_apply' | 'view' });
//
// - Inserts into public.apply_clicks (user_id, source_channel, external_job_id,
//   job_url, kind).
// - Returns the UUID of the inserted row, or null on any failure.
// - Anonymous users: the insert is attempted; if RLS rejects (anonymous users
//   have no user_id to satisfy the WITH CHECK), we log a console.warn and
//   return null — the calling click handler MUST NOT be blocked.
// - Hard timeout: the insert is raced against a 200 ms deadline; if Supabase
//   hasn't responded by then we still fire the action and the result resolves
//   to null when it eventually comes back.

import { useCallback } from 'react';

import { supabase } from '../../../lib/supabase';
import type { Job } from '../components/JobCard';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ApplyClickArgs {
  job:  Job;
  kind: 'optimize_and_apply' | 'view';
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useApplyClickLogger(): (args: ApplyClickArgs) => Promise<string | null> {
  return useCallback(async ({ job, kind }: ApplyClickArgs): Promise<string | null> => {
    // Supabase does NOT auto-populate columns from the session JWT — we must
    // read the session and supply user_id explicitly so the RLS
    // WITH CHECK (auth.uid() = user_id) can pass.
    const { data: { session } } = await supabase.auth.getSession();
    const userId = session?.user?.id ?? null;

    const payload = {
      user_id:         userId,
      source_channel:  job.source,
      external_job_id: job.external_job_id ?? null,
      job_url:         job.url,
      kind,
    };

    // Race the DB insert against a 200 ms timeout so we never block the UI.
    const insertPromise = supabase
      .from('apply_clicks')
      .insert(payload)
      .select('id')
      .single()
      .then(({ data, error }) => {
        if (error) {
          // RLS rejection (anon user) or any other DB error → warn, don't throw.
          console.warn('[useApplyClickLogger] insert failed:', error.message);
          return null;
        }
        return (data as { id: string } | null)?.id ?? null;
      })
      .catch((err: unknown) => {
        console.warn('[useApplyClickLogger] unexpected error:', err);
        return null;
      });

    const timeoutPromise = new Promise<null>((resolve) =>
      setTimeout(() => resolve(null), 200),
    );

    return Promise.race([insertPromise, timeoutPromise]);
  }, []);
}
